/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dashboard } from '@gaia/shared/data-models';
import { Injectable } from '@angular/core';
import { AnalyticsService } from '@titan/devkit/analytics';
import { UserCookieService } from '@titan/devkit/user';

@Injectable()
export class EventsService {
    appName: string;
    eventPrefixCode: string;

    constructor(protected analyticsService: AnalyticsService, private userCookie: UserCookieService) {
        this.appName = 'Atlas';
        this.eventPrefixCode = '732';
    }

    private sendEvent(event: any) {
        return this.analyticsService.eventTrack({
            category: this.appName,
            ...event,
        });
    }

    loginSuccess() {
        const { cn: username, authenticationStrategy } = this.userCookie.getTitanCookie()!;
        const oktaLogin = authenticationStrategy === 'okta';
        const action = oktaLogin ? 'Okta Login' : 'X509 Login';
        const codeSuffix = oktaLogin ? '0022' : '0001';
        return this.sendEvent({
            action,
            label: `${username} logged in`,
            code: `${this.eventPrefixCode}-${codeSuffix}`,
        });
    }

    loginFailure(msg: string) {
        return this.sendEvent({
            action: 'Login',
            label: `A user failed to login`,
            value: msg,
            code: `${this.eventPrefixCode}-0002`,
        });
    }

    selectedLocale(username: string | null, locale: string) {
        return this.sendEvent({
            action: 'Locale selected',
            label: `${username} selected ${locale}`,
            code: `${this.eventPrefixCode}-0003`,
        });
    }

    toggledTheme(username: string) {
        return this.sendEvent({
            action: 'Theme toggled',
            label: `${username} toggled dark theme`,
            code: `${this.eventPrefixCode}-0004`,
        });
    }

    sideNavigationExpanded(username: string) {
        return this.sendEvent({
            action: 'Side navigation expanded',
            label: `${username} expanded side navigation`,
            code: `${this.eventPrefixCode}-0007`,
        });
    }

    sideNavigationClosed(username: string) {
        return this.sendEvent({
            action: 'Side navigation closed',
            label: `${username} closed side navigation`,
            code: `${this.eventPrefixCode}-0008`,
        });
    }

    sideNavigationSelected(username: string, link: string) {
        return this.sendEvent({
            action: 'Side navigation selected',
            label: `${username} selected ${link}`,
            code: `${this.eventPrefixCode}-0010`,
        });
    }

    dashboardSelected(username: string, dashboard: Dashboard) {
        return this.sendEvent({
            action: 'Dashboard selected',
            label: `${username} selected ${dashboard.id}`,
            code: `${this.eventPrefixCode}-0011`,
        });
    }

    dashboardLoaded(username: string, dashboard: Dashboard) {
        return this.sendEvent({
            action: 'Dashboard loaded successfully',
            label: `${username} loaded dashboard:${dashboard.id} successfully`,
            code: `${this.eventPrefixCode}-0012`,
        });
    }

    dashboardFailedToLoad(username: string, dashboard: Dashboard) {
        return this.sendEvent({
            action: 'Dashboard load failed',
            label: `${username} failed to load dashboard:${dashboard.id}`,
            code: `${this.eventPrefixCode}-0013`,
        });
    }

    dashboardCreated(username: string, dashboard: Dashboard) {
        return this.sendEvent({
            action: 'Dashboard created',
            label: `${username} created dashboard:${dashboard.id}`,
            code: `${this.eventPrefixCode}-0014`,
        });
    }

    dashboardDeleted(username: string, dashboardId: string) {
        return this.sendEvent({
            action: 'Dashboard deleted',
            label: `${username} deleted dashboard:${dashboardId}`,
            code: `${this.eventPrefixCode}-0015`,
        });
    }

    logout(username: string) {
        return this.sendEvent({
            action: 'Logout',
            label: `${username} has logged out`,
            code: `${this.eventPrefixCode}-0021`,
        });
    }
}
